import { CurrencyCode, CurrencyDescription, currencyList } from './supported.currencies'

/**
 * List of country codes supported by Ankor Pay.
 */
export enum CountryCode {
  // United Arab Emirates
  AE = 'AE',
  // Australia
  AU = 'AU',
  // Austria
  AT = 'AT',
  // Belgium
  BE = 'BE',
  // Bulgaria
  BG = 'BG',
  // Brazil
  BR = 'BR',
  // Canada
  CA = 'CA',
  // Switzerland
  CH = 'CH',
  // China
  CN = 'CN',
  // Cyprus
  CY = 'CY',
  // Czech Republic
  CZ = 'CZ',
  // Germany
  DE = 'DE',
  // Denmark
  DK = 'DK',
  // Spain
  ES = 'ES',
  // Estonia
  EE = 'EE',
  // Finland
  FI = 'FI',
  // France
  FR = 'FR',
  // United Kingdom
  GB = 'GB',
  // Greece
  GR = 'GR',
  // Hong Kong
  HK = 'HK',
  // Croatia
  HR = 'HR',
  // Hungary
  HU = 'HU',
  // Indonesia
  ID = 'ID',
  // Ireland
  IE = 'IE',
  // Israel
  IL = 'IL',
  // Iceland
  IS = 'IS',
  // Italy
  IT = 'IT',
  // Japan
  JP = 'JP',
  // South Korea
  KR = 'KR',
  // Lithuania
  LT = 'LT',
  // Luxembourg
  LU = 'LU',
  // Latvia
  LV = 'LV',
  // Mexico
  MX = 'MX',
  // Malta
  MT = 'MT',
  // Netherlands
  NL = 'NL',
  // Norway
  NO = 'NO',
  // New Zealand
  NZ = 'NZ',
  // Philippines
  PH = 'PH',
  // Poland
  PL = 'PL',
  // Portugal
  PT = 'PT',
  // Romania
  RO = 'RO',
  // Slovenia
  SI = 'SI',
  // Slovakia
  SK = 'SK',
  // Singapore
  SG = 'SG',
  // Sweden
  SE = 'SE',
  // Thailand
  TH = 'TH',
  // United States
  US = 'US',
  // Vietnam
  VN = 'VN',
  // South Africa
  ZA = 'ZA',
}

export type CountryDescription = {
  country: string
  countryCode: CountryCode
  flag: string
  currencies: CurrencyDescription[]
}

/**
 * List of countries and it's full details supported by Ankor Pay.
 */
export const countryList: Record<CountryCode, CountryDescription> = {
  [CountryCode.AE]: {
    country: 'United Arab Emirates',
    countryCode: CountryCode.AE,
    flag: '🇦🇪',
    currencies: [currencyList[CurrencyCode.AED]],
  },
  [CountryCode.AT]: {
    country: 'Austria',
    countryCode: CountryCode.AT,
    flag: '🇦🇹',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.AU]: {
    country: 'Australia',
    countryCode: CountryCode.AU,
    flag: '🇦🇺',
    currencies: [currencyList[CurrencyCode.AUD]],
  },
  [CountryCode.BE]: {
    country: 'Belgium',
    countryCode: CountryCode.BE,
    flag: '🇧🇪',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.BG]: {
    country: 'Bulgaria',
    countryCode: CountryCode.BG,
    flag: '🇧🇬',
    currencies: [currencyList[CurrencyCode.BGN]],
  },
  [CountryCode.BR]: {
    country: 'Brazil',
    countryCode: CountryCode.BR,
    flag: '🇧🇷',
    currencies: [currencyList[CurrencyCode.BRL]],
  },
  [CountryCode.CA]: {
    country: 'Canada',
    countryCode: CountryCode.CA,
    flag: '🇨🇦',
    currencies: [currencyList[CurrencyCode.CAD]],
  },
  [CountryCode.CH]: {
    country: 'Switzerland',
    countryCode: CountryCode.CH,
    flag: '🇨🇭',
    currencies: [currencyList[CurrencyCode.CHF]],
  },
  [CountryCode.CN]: {
    country: 'China',
    countryCode: CountryCode.CN,
    flag: '🇨🇳',
    currencies: [currencyList[CurrencyCode.CNY]],
  },
  [CountryCode.CY]: {
    country: 'Cyprus',
    countryCode: CountryCode.CY,
    flag: '🇨🇾',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.CZ]: {
    country: 'Czech Republic',
    countryCode: CountryCode.CZ,
    flag: '🇨🇿',
    currencies: [currencyList[CurrencyCode.CZK]],
  },
  [CountryCode.DE]: {
    country: 'Germany',
    countryCode: CountryCode.DE,
    flag: '🇪🇺',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.DK]: {
    country: 'Denmark',
    countryCode: CountryCode.DK,
    flag: '🇩🇰',
    currencies: [
      currencyList[CurrencyCode.DKK],
      currencyList[CurrencyCode.EUR],
      currencyList[CurrencyCode.AED],
      currencyList[CurrencyCode.AUD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.CHF],
      currencyList[CurrencyCode.CNY],
      currencyList[CurrencyCode.CZK],
      currencyList[CurrencyCode.GBP],
      currencyList[CurrencyCode.HKD],
      currencyList[CurrencyCode.HUF],
      currencyList[CurrencyCode.ILS],
      currencyList[CurrencyCode.JPY],
      currencyList[CurrencyCode.MXN],
      currencyList[CurrencyCode.NOK],
      currencyList[CurrencyCode.NZD],
      currencyList[CurrencyCode.PLN],
      currencyList[CurrencyCode.RON],
      currencyList[CurrencyCode.SEK],
      currencyList[CurrencyCode.SGD],
      currencyList[CurrencyCode.USD],
      currencyList[CurrencyCode.ZAR],
    ],
  },
  [CountryCode.EE]: {
    country: 'Estonia',
    countryCode: CountryCode.EE,
    flag: '🇪🇪',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.ES]: {
    country: 'Spain',
    countryCode: CountryCode.ES,
    flag: '🇪🇸',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.FI]: {
    country: 'Finland',
    countryCode: CountryCode.FI,
    flag: '🇫🇮',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.FR]: {
    country: 'France',
    countryCode: CountryCode.FR,
    flag: '🇫🇷',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.GB]: {
    country: 'United Kingdom',
    countryCode: CountryCode.GB,
    flag: '🇬🇧',
    currencies: [currencyList[CurrencyCode.GBP]],
  },
  [CountryCode.GR]: {
    country: 'Greece',
    countryCode: CountryCode.GR,
    flag: '🇬🇷',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.HK]: {
    country: 'Hong Kong',
    countryCode: CountryCode.HK,
    flag: '🇭🇰',
    currencies: [
      currencyList[CurrencyCode.HKD],
      currencyList[CurrencyCode.CNY],
      currencyList[CurrencyCode.USD],
      currencyList[CurrencyCode.EUR],
      currencyList[CurrencyCode.AUD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.CHF],
      currencyList[CurrencyCode.GBP],
      currencyList[CurrencyCode.JPY],
      currencyList[CurrencyCode.NZD],
      currencyList[CurrencyCode.SGD],
    ],
  },
  [CountryCode.HR]: {
    country: 'Croatia',
    countryCode: CountryCode.HR,
    flag: '🇭🇷',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.HU]: {
    country: 'Hungary',
    countryCode: CountryCode.HU,
    flag: '🇭🇺',
    currencies: [currencyList[CurrencyCode.HUF]],
  },
  [CountryCode.ID]: {
    country: 'Indonesia',
    countryCode: CountryCode.ID,
    flag: '🇮🇩',
    currencies: [currencyList[CurrencyCode.IDR]],
  },
  [CountryCode.IE]: {
    country: 'Ireland',
    countryCode: CountryCode.IE,
    flag: '🇮🇪',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.IL]: {
    country: 'Israel',
    countryCode: CountryCode.IL,
    flag: '🇮🇱',
    currencies: [currencyList[CurrencyCode.ILS]],
  },
  [CountryCode.IS]: {
    country: 'Iceland',
    countryCode: CountryCode.IS,
    flag: '🇮🇸',
    currencies: [currencyList[CurrencyCode.ISK]],
  },
  [CountryCode.IT]: {
    country: 'Italy',
    countryCode: CountryCode.IT,
    flag: '🇮🇹',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.JP]: {
    country: 'Japan',
    countryCode: CountryCode.JP,
    flag: '🇯🇵',
    currencies: [currencyList[CurrencyCode.JPY]],
  },
  [CountryCode.KR]: {
    country: 'South Korea',
    countryCode: CountryCode.KR,
    flag: '🇰🇷',
    currencies: [currencyList[CurrencyCode.KRW]],
  },
  [CountryCode.LT]: {
    country: 'Lithuania',
    countryCode: CountryCode.LT,
    flag: '🇱🇹',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.LU]: {
    country: 'Luxembourg',
    countryCode: CountryCode.LU,
    flag: '🇱🇺',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.LV]: {
    country: 'Latvia',
    countryCode: CountryCode.LV,
    flag: '🇱🇻',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.MT]: {
    country: 'Malta',
    countryCode: CountryCode.MT,
    flag: '🇲🇹',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.MX]: {
    country: 'Mexico',
    countryCode: CountryCode.MX,
    flag: '🇲🇽',
    currencies: [currencyList[CurrencyCode.MXN]],
  },
  [CountryCode.NL]: {
    country: 'Netherlands',
    countryCode: CountryCode.NL,
    flag: '🇳🇱',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.NO]: {
    country: 'Norway',
    countryCode: CountryCode.NO,
    flag: '🇳🇴',
    currencies: [currencyList[CurrencyCode.NOK]],
  },
  [CountryCode.NZ]: {
    country: 'New Zealand',
    countryCode: CountryCode.NZ,
    flag: '🇳🇿',
    currencies: [currencyList[CurrencyCode.NZD]],
  },
  [CountryCode.PH]: {
    country: 'Philippines',
    countryCode: CountryCode.PH,
    flag: '🇵🇭',
    currencies: [currencyList[CurrencyCode.PHP]],
  },
  [CountryCode.PL]: {
    country: 'Poland',
    countryCode: CountryCode.PL,
    flag: '🇵🇱',
    currencies: [currencyList[CurrencyCode.PLN]],
  },
  [CountryCode.PT]: {
    country: 'Portugal',
    countryCode: CountryCode.PT,
    flag: '🇵🇹',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.RO]: {
    country: 'Romania',
    countryCode: CountryCode.RO,
    flag: '🇷🇴',
    currencies: [currencyList[CurrencyCode.RON]],
  },
  [CountryCode.SE]: {
    country: 'Sweden',
    countryCode: CountryCode.SE,
    flag: '🇸🇪',
    currencies: [currencyList[CurrencyCode.SEK]],
  },
  [CountryCode.SI]: {
    country: 'Slovenia',
    countryCode: CountryCode.SI,
    flag: '🇸🇮',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.SK]: {
    country: 'Slovakia',
    countryCode: CountryCode.SK,
    flag: '🇸🇰',
    currencies: [currencyList[CurrencyCode.EUR]],
  },
  [CountryCode.SG]: {
    country: 'Singapore',
    countryCode: CountryCode.SG,
    flag: '🇸🇬',
    currencies: [
      currencyList[CurrencyCode.SGD],
      currencyList[CurrencyCode.USD],
      currencyList[CurrencyCode.AUD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.CAD],
      currencyList[CurrencyCode.GBP],
      currencyList[CurrencyCode.HKD],
      currencyList[CurrencyCode.JPY],
      currencyList[CurrencyCode.NOK],
      currencyList[CurrencyCode.NZD],
      currencyList[CurrencyCode.SEK],
    ],
  },
  [CountryCode.TH]: {
    country: 'Thailand',
    countryCode: CountryCode.TH,
    flag: '🇹🇭',
    currencies: [currencyList[CurrencyCode.THB]],
  },
  [CountryCode.US]: {
    country: 'United States',
    countryCode: CountryCode.US,
    flag: '🇺🇸',
    currencies: [currencyList[CurrencyCode.USD]],
  },
  [CountryCode.VN]: {
    country: 'Vietnam',
    countryCode: CountryCode.VN,
    flag: '🇻🇳',
    currencies: [currencyList[CurrencyCode.VND]],
  },
  [CountryCode.ZA]: {
    country: 'South Africa',
    countryCode: CountryCode.ZA,
    flag: '🇿🇦',
    currencies: [currencyList[CurrencyCode.ZAR]],
  },
}

export type CountryPartialRecord = Partial<Record<CountryCode, CountryDescription>>

/**
 * The ones not commented are listed as the priority for V1
 * NOTE FOR LATER:
 * When introducing other countries that share the same currency, currently the Balance type only knows
 * about the currency code, not the country code. This will need to be updated to support multiple countries
 * with the same currency code otherwise the front end will show the wrong flag for the currency
 * of the global account actually selected.
 * Eg, multiple countries with EUR will just show the flag of the first country in the list rather than the selected one.
 */
export const globalAccountCountries: Partial<Record<CountryCode, CountryDescription>> = {
  [CountryCode.US]: countryList[CountryCode.US],
  [CountryCode.NL]: countryList[CountryCode.NL],
  [CountryCode.GB]: countryList[CountryCode.GB],
  [CountryCode.AU]: countryList[CountryCode.AU],
  [CountryCode.DK]: countryList[CountryCode.DK],
  // [CountryCode.CA]: countryList[CountryCode.CA],
  // [CountryCode.EE]: countryList[CountryCode.EE],
  // [CountryCode.DE]: countryList[CountryCode.DE],
  // [CountryCode.HK]: countryList[CountryCode.HK],
  // [CountryCode.ID]: countryList[CountryCode.ID],
  // [CountryCode.IL]: countryList[CountryCode.IL],
  // [CountryCode.MX]: countryList[CountryCode.MX],
  // [CountryCode.NZ]: countryList[CountryCode.NZ],
  // [CountryCode.PH]: countryList[CountryCode.PH],
  // [CountryCode.SG]: countryList[CountryCode.SG],
  // [CountryCode.AE]: countryList[CountryCode.AE],
}

// Not used in the app,  but is a reference to what AnkorPay supports for connected accounts/issue cards etcexport const connectedAccountCountries: Partial<Record<CountryCode, CountryDescription>> = {
export const connectedAccountCountries: Partial<Record<CountryCode, CountryDescription>> = {
  [CountryCode.AT]: countryList[CountryCode.AT],
  [CountryCode.AU]: countryList[CountryCode.AU],
  [CountryCode.BE]: countryList[CountryCode.BE],
  [CountryCode.BG]: countryList[CountryCode.BG],
  [CountryCode.CA]: countryList[CountryCode.CA],
  [CountryCode.CH]: countryList[CountryCode.CH],
  [CountryCode.CY]: countryList[CountryCode.CY],
  [CountryCode.CZ]: countryList[CountryCode.CZ],
  [CountryCode.DK]: countryList[CountryCode.DK],
  [CountryCode.DE]: countryList[CountryCode.DE],
  [CountryCode.EE]: countryList[CountryCode.EE],
  [CountryCode.ES]: countryList[CountryCode.ES],
  [CountryCode.FI]: countryList[CountryCode.FI],
  [CountryCode.FR]: countryList[CountryCode.FR],
  [CountryCode.GB]: countryList[CountryCode.GB],
  [CountryCode.GR]: countryList[CountryCode.GR],
  [CountryCode.HK]: countryList[CountryCode.HK],
  [CountryCode.HR]: countryList[CountryCode.HR],
  [CountryCode.HU]: countryList[CountryCode.HU],
  [CountryCode.IE]: countryList[CountryCode.IE],
  [CountryCode.IS]: countryList[CountryCode.IS],
  [CountryCode.IT]: countryList[CountryCode.IT],
  [CountryCode.LV]: countryList[CountryCode.LV],
  [CountryCode.LT]: countryList[CountryCode.LT],
  [CountryCode.LU]: countryList[CountryCode.LU],
  [CountryCode.MT]: countryList[CountryCode.MT],
  [CountryCode.NL]: countryList[CountryCode.NL],
  [CountryCode.NO]: countryList[CountryCode.NO],
  [CountryCode.NZ]: countryList[CountryCode.NZ],
  [CountryCode.PL]: countryList[CountryCode.PL],
  [CountryCode.PT]: countryList[CountryCode.PT],
  [CountryCode.RO]: countryList[CountryCode.RO],
  [CountryCode.SG]: countryList[CountryCode.SG],
  [CountryCode.SI]: countryList[CountryCode.SI],
  [CountryCode.SK]: countryList[CountryCode.SK],
  [CountryCode.SE]: countryList[CountryCode.SE],
  [CountryCode.US]: countryList[CountryCode.US],
}

export const getCountryFlagByCurrencyCode = (currencyCode: CurrencyCode) => {
  const isEuroCurrency = currencyCode === CurrencyCode.EUR
  if (isEuroCurrency) {
    return '🇪🇺'
  }

  return Object.values(countryList).find((c) => c.currencies[0].code === currencyCode)?.flag
}
